import { useTranslation } from '@jq4race/i18n';
import { type PasswordRule, validatePassword } from '@jq4race/shared';
import { cn, Input, Label } from '@jq4race/ui';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { useState } from 'react';

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  showCriteria?: boolean;
  autoComplete?: 'current-password' | 'new-password';
  required?: boolean;
}

const ALL_RULES: PasswordRule[] = ['min_length', 'uppercase', 'number', 'special'];

const RULE_KEYS: Record<PasswordRule, string> = {
  min_length: 'shared.passwordRules.minLength',
  uppercase: 'shared.passwordRules.uppercase',
  number: 'shared.passwordRules.number',
  special: 'shared.passwordRules.special',
};

export function PasswordField({
  id,
  label,
  value,
  onChange,
  showCriteria = false,
  autoComplete,
  required,
}: PasswordFieldProps) {
  const { t } = useTranslation('auth');
  const [show, setShow] = useState(false);
  const violations = new Set(validatePassword(value).map((v) => v.rule));

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required={required}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-jq-graphite transition hover:text-jq-navy"
          aria-label={show ? t('shared.hidePassword') : t('shared.showPassword')}
          tabIndex={-1}
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {showCriteria ? (
        <ul className="mt-1 grid gap-1 text-[13px]">
          {ALL_RULES.map((rule) => {
            const empty = value.length === 0;
            const violated = violations.has(rule);
            const state: 'idle' | 'pass' | 'fail' = empty ? 'idle' : violated ? 'fail' : 'pass';
            return (
              <li key={rule} className="flex items-center gap-2 transition-colors duration-200">
                <span
                  className={cn(
                    'inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors duration-200',
                    state === 'idle' && 'bg-jq-surface-3 text-jq-stone',
                    state === 'pass' && 'bg-success/15 text-success',
                    state === 'fail' && 'bg-destructive/15 text-destructive',
                  )}
                >
                  {state === 'pass' ? <Check className="size-3" /> : <X className="size-3" />}
                </span>
                <span
                  className={cn(
                    'transition-colors duration-200',
                    state === 'idle' && 'text-jq-graphite',
                    state === 'pass' && 'text-success',
                    state === 'fail' && 'text-destructive',
                  )}
                >
                  {t(RULE_KEYS[rule])}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
